import axios from 'axios'
import moment from 'moment'
import pubsub from 'pubsub-js'
import { useEffect, useState } from 'react'
import { useHistory, Link } from 'react-router-dom'
import { Badge, Button, Col, Row } from 'reactstrap'
import { useQueryClient } from '@tanstack/react-query'
import ContentWrapper from '../../../components/Layout/ContentWrapper'
import Loader from '../../../components/Layout/Loader'
import { APPLICANTS_API } from '../../../utils/consts'
import { formatKosovoDate } from '../../../utils/dateUtils'
import ApplicantDataTable from '../Components/ApplicantDataTable'
import ApplicantModal from '../Components/ApplicantModal'
import ScheduleInterviewModal from '../Components/ScheduleInterviewModal'
import ApplicantHistoryLegend from '../Components/ApplicantHistoryLegend'
import { buttonsApplicants, requiredApplicantFields } from '../enums/applicantsInput'
import { statusEnumSQ } from '../enums/statusEnumSQ'
import { useGetApplicantHistory, dismissBlacklistAlert, dismissReturningEmployeeAlert } from '../applicantsRequests'
import Swal from 'sweetalert2'

const blacklistMatchReasonLabels = {
  name: 'full name',
  email: 'email',
  both: 'full name and email'
}

const returningEmployeeMatchTypeLabels = {
  identity_card: 'Confirmed match via national ID',
  name: 'Potential match via full name'
}

const alertActionColumnStyle = {
  minWidth: '240px',
  flexShrink: 0,
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center'
}

const Applicant = ({ user, id }) => {
  const [loading, setLoader] = useState(true)
  const [applicant, setApplicant] = useState(null)
  const [applicantStatus, setApplicantStatus] = useState('')
  const [exEmployee, setExEmployee] = useState(false)
  const [formerEmployeeProfiles, setFormerEmployeeProfiles] = useState([])
  const [show, setShow] = useState(false)
  const [description, setDescription] = useState('')
  const [testResult, setTestResult] = useState()
  const [status, setStatus] = useState('new')
  const [newStatus, setNewStatus] = useState('')
  const [languageLevel, setLanguageLevel] = useState()
  const [testPassability, setTestPassability] = useState(undefined)
  const [error, setError] = useState(false)
  const [applicantData, setApplicantData] = useState()
  const [sendDirectToTesting, setSendDirectToTesting] = useState(false)
  const [showInterviewModal, setShowInterviewModal] = useState(false)
  const history = useHistory()
  const queryClient = useQueryClient()

  // Fetch applicant history
  const { data: historyData, isLoading: historyLoading } = useGetApplicantHistory(id)
  const applicantHistory = historyData?.history || []

  useEffect(() => {
    if (id) {
      getApplicant(id)
    }
  }, [id])

  const getApplicant = (id) => {
    setLoader(true)
    axios
      .get(`${APPLICANTS_API}/applicants/getOne`, {
        params: { id },
        withCredentials: false
      })
      .then((data) => {
        if (data.data.success) {
          setApplicant(data.data.applicant)
          setApplicantData(data.data.applicant)
          setApplicantStatus(data.data.applicant.status)
          setExEmployee(
            data.data.applicant.ex_employee && !data.data.applicant.ex_employee_dismissed
          )
          setFormerEmployeeProfiles(data.data.formerEmployeeProfiles || [])
        }
      })
      .catch((e) => console.log('error', error))
      .finally(() => setLoader(false))
  }

  const rollbackApplicantStatus = async () => {
    try {
      const response = await axios.patch(`${APPLICANTS_API}/applicants/rollbackToNew/${id}`, {}, { withCredentials: false })
      if (response.data.success) {
        pubsub.publish('showNotification', {
          message: `Statusi kaloj ne aplikues`,
          type: 'success'
        })
        queryClient.invalidateQueries({ queryKey: ['applicants'] })
        queryClient.invalidateQueries({ queryKey: ['applicantHistory'] })
        queryClient.invalidateQueries({ queryKey: ['applicantHistoryById', id] })
        getApplicant(id)
      } else {
        pubsub.publish('showNotification', {
          message: res.data.message,
          type: 'error'
        })
      }
    } catch (error) {
      pubsub.publish('showNotification', {
        message: error.message,
        type: 'error'
      })
    }
  }

  const handleDismissBlacklistAlert = async () => {
    const result = await Swal.fire({
      title: 'Verify applicant',
      text: 'Confirm this person is not in the blacklist and remove the alert?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, remove alert',
      cancelButtonText: 'Cancel'
    })

    if (!result.isConfirmed) return

    try {
      const response = await dismissBlacklistAlert(id)
      if (response.success) {
        pubsub.publish('showNotification', {
          message: 'Blacklist alert removed',
          type: 'success'
        })
        queryClient.invalidateQueries({ queryKey: ['applicants'] })
        queryClient.invalidateQueries({ queryKey: ['applicantHistoryById', id] })
        getApplicant(id)
      } else {
        pubsub.publish('showNotification', {
          message: response.message || 'Failed to remove blacklist alert',
          type: 'error'
        })
      }
    } catch (error) {
      pubsub.publish('showNotification', {
        message: error.message,
        type: 'error'
      })
    }
  }

  const handleDismissReturningEmployeeAlert = async () => {
    const result = await Swal.fire({
      title: 'Verify applicant',
      text: 'Confirm this person is not a former employee and remove the alert?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, remove alert',
      cancelButtonText: 'Cancel'
    })

    if (!result.isConfirmed) return

    try {
      const response = await dismissReturningEmployeeAlert(id)
      if (response.success) {
        pubsub.publish('showNotification', {
          message: 'Former employee alert removed',
          type: 'success'
        })
        queryClient.invalidateQueries({ queryKey: ['applicants'] })
        queryClient.invalidateQueries({ queryKey: ['applicantHistoryById', id] })
        getApplicant(id)
      } else {
        pubsub.publish('showNotification', {
          message: response.message || 'Failed to remove former employee alert',
          type: 'error'
        })
      }
    } catch (error) {
      pubsub.publish('showNotification', {
        message: error.message,
        type: 'error'
      })
    }
  }

  // const changeApplicantStatus = async (item = false) => {
  //   try {
  //     const requestBody = item
  //       ? { ...applicantData }
  //       : {
  //           ...applicantData,
  //           status: newStatus || status,
  //           description: description,
  //           ...(status === 'waiting_list' && {
  //             test_level: languageLevel,
  //             test_result: testPassability,
  //             test_score: testResult
  //           })
  //         }

  //     if ((applicant.status !== status || applicant.status !== newStatus) && requestBody.status_changes) {
  //       requestBody.status_changes = [
  //         ...(applicant.status_changes || []),
  //         {
  //           from_status: applicant.status,
  //           to_status: newStatus || status,
  //           changed_at: new Date(Date.now()),
  //           changed_by: user.user_id
  //         }
  //       ]
  //     }

  //     if ((status === 'recruited' || newStatus === 'recruited') && requiredApplicantFields.length) {
  //       const missingFields = requiredApplicantFields.filter(
  //         (oneReq) =>
  //           applicantData[oneReq.value] === '' ||
  //           applicantData[oneReq.value] === undefined ||
  //           applicantData[oneReq.value] === null ||
  //           (oneReq.value === 'bank_account_no' && applicantData[oneReq.value]?.length !== 16) ||
  //           (oneReq.value === 'password' && applicantData[oneReq.value]?.length < 8)
  //       )

  //       if (missingFields.length) {
  //         pubsub.publish('showNotification', {
  //           message: `Fushat ${missingFields.map((req) => req.text)} jane te nevojshme`,
  //           type: 'error'
  //         })
  //         return
  //       }
  //     }

  //     const endpoint =
  //       status === 'recruited' || newStatus === 'recruited'
  //         ? `${APPLICANTS_API}/applicants/createUserFromApplicant?id=${id}`
  //         : `${APPLICANTS_API}/applicants/updateStatus?id=${id}`

  //     const response = await (status === 'recruited' || newStatus === 'recruited'
  //       ? axios.post(endpoint, requestBody)
  //       : axios.patch(endpoint, requestBody))

  //     if (response.data.success) {
  //       const notificationText = item
  //         ? 'Të dhënat u përditësuan!'
  //         : `Statusi u ndryshua në ${statusEnumSQ[status].charAt(0).toLowerCase() + statusEnumSQ[status].slice(1)}`
  //       pubsub.publish('showNotification', { message: notificationText, type: 'success' })
  //       if (!item) setApplicantStatus(response.data.applicant.status)
  //       getApplicant(id)
  //     } else {
  //       throw new Error(response.data.message)
  //     }
  //   } catch (error) {
  //     pubsub.publish('showNotification', { message: error.message, type: 'error' })
  //   }
  // }

  const changeApplicantStatus = async (item = false) => {
    let body
    if (item) {
      body = { ...applicantData }
    } else {
      body = {
        ...applicantData,
        status: newStatus ? newStatus : status,
        description: description
      }
    }

    if (body.birthdate) {
      body.birthdate = moment(body.birthdate).format('YYYY-MM-DD')
    }
    if (body.first_call) {
      body.first_call = moment(body.first_call).format('YYYY-MM-DD')
    }
    if (applicant.status != status || applicant.status != newStatus) {
      if (body?.status_changes) {
        body.status_changes = [
          ...applicant?.status_changes,
          {
            from_status: applicant?.status,
            to_status: newStatus ? newStatus : status,
            changed_at: new Date(Date.now()),
            changed_by: user.user_id
          }
        ]
      } else {
        body.status_changes = [
          {
            from_status: applicant?.status,
            to_status: newStatus ? newStatus : status,
            changed_at: new Date(Date.now()),
            changed_by: user.user_id
          }
        ]
      }
    }

    if (status === 'waiting_list' || status === 'failed') {
      body.test_level = languageLevel
      body.test_result = testPassability
      body.test_score = testResult
    }
    try {
      if (status === 'recruited' || newStatus === 'recruited') {
        const reqFields = requiredApplicantFields.filter(
          (oneReq) =>
            applicantData[oneReq.value] === '' ||
            applicantData[oneReq.value] === undefined ||
            applicantData[oneReq.value] === null ||
            (oneReq.value === 'bank_account_no' && applicantData[oneReq.value]?.length !== 16) ||
            (oneReq.value === 'password' && applicantData[oneReq.value]?.length < 8)
        )
        if (reqFields.length) {
          pubsub.publish('showNotification', {
            message: `Fields
            ${reqFields.map((req) => req.text)} are required`,
            type: 'error'
          })

          return
        } else {
          const res = await axios.post(`${APPLICANTS_API}/applicants/createUserFromApplicant?id=${id}`, body, { withCredentials: false })
          if (res.data.success) {
            pubsub.publish('showNotification', {
              message: `Status changed to ${statusEnumSQ[status].charAt(0).toLowerCase() + statusEnumSQ[status].slice(1)}`,
              type: 'success'
            })
            pubsub.publish('showNotification', {
              message: 'Profile is activated.',
              type: 'success'
            })
            queryClient.invalidateQueries({ queryKey: ['applicants'] })
            queryClient.invalidateQueries({ queryKey: ['applicantHistory'] })
            getApplicant(id)
          } else {
            pubsub.publish('showNotification', {
              message: res.data.message,
              type: 'error'
            })
          }
        }
      } else {
        const { data } = await axios.patch(`${APPLICANTS_API}/applicants/updateStatus?id=${id}`, body, { withCredentials: false })
        if (data.success) {
          let notificationText = `Status changed to ${statusEnumSQ[status].charAt(0).toLowerCase() + statusEnumSQ[status].slice(1)}`

          if (item) {
            notificationText = 'Data updated!'
          }
          pubsub.publish('showNotification', {
            message: notificationText,
            type: 'success'
          })
          queryClient.invalidateQueries({ queryKey: ['applicants'] })
          queryClient.invalidateQueries({ queryKey: ['applicantHistory'] })
          queryClient.invalidateQueries({ queryKey: ['applicantHistoryById', id] })
          setApplicantStatus(data.applicant.status)
          getApplicant(id)
        } else {
          throw Error(data.message)
        }
      }
    } catch (error) {
      pubsub.publish('showNotification', {
        message: error.message,
        type: 'error'
      })
    }
  }

  const handleClose = () => {
    setDescription('')
    setTestResult()
    setStatus('new')
    setLanguageLevel('')
    setTestPassability('')
    setNewStatus('')
    setSendDirectToTesting(false)
    setShow(false)
  }

  const handleOpen = () => {
    setShow(true)
  }

  return (
    user && (
      <ContentWrapper>
        <ScheduleInterviewModal
          show={showInterviewModal}
          onHide={() => setShowInterviewModal(false)}
          applicantId={id}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['applicants'] })
            queryClient.invalidateQueries({ queryKey: ['applicantHistory'] })
            queryClient.invalidateQueries({ queryKey: ['applicantHistoryById', id] })
            getApplicant(id)
          }}
        />

        <ApplicantModal
          show={show}
          setShow={setShow}
          handleClose={handleClose}
          status={status}
          testResult={testResult}
          setTestResult={setTestResult}
          languageLevel={languageLevel}
          setLanguageLevel={setLanguageLevel}
          testPassability={testPassability}
          setTestPassability={setTestPassability}
          newStatus={newStatus}
          setNewStatus={setNewStatus}
          description={description}
          setDescription={setDescription}
          error={error}
          setError={setError}
          changeApplicantStatus={changeApplicantStatus}
          buttonsApplicants={buttonsApplicants}
          sendDirectToTesting={sendDirectToTesting}
          setSendDirectToTesting={setSendDirectToTesting}
        />

        {!loading ? (
          <div>
            <div className='content-heading d-flex justify-content-between'>
              <div className='d-flex align-items-center flex-wrap'>
                {applicant && (
                  <span className='mr-2'>
                    {applicant.firstName} {applicant.lastName}
                  </span>
                )}
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  {applicant?.blacklist_match && !applicant?.blacklist_match_dismissed && (
                    <span
                      className='badge bg-warning'
                      style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-4px',
                        padding: '3px 6px',
                        borderRadius: '3px',
                        fontSize: '9px',
                        fontWeight: 600,
                        letterSpacing: '0.3px',
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap',
                        zIndex: 1
                      }}
                    >
                      Potential in blacklist
                    </span>
                  )}
                  <Badge
                    color='info'
                    style={{
                      padding: '10px 20px',
                      borderRadius: '4px',
                      fontSize: '18px',
                      fontWeight: '500'
                    }}
                  >
                    {statusEnumSQ[applicantStatus]}
                  </Badge>
                </div>
                {applicantStatus === 'interview' && applicant?.interview_date && (
                  <span className='ml-2' style={{ fontSize: '16px', color: '#6c757d' }}>
                    {formatKosovoDate(applicant.interview_date)}
                  </span>
                )}
                {applicantStatus === 'recruited' && (
                  <Badge
                    color='warning'
                    style={{
                      padding: '10px 20px',
                      borderRadius: '4px',
                      fontSize: '18px'
                    }}
                    className='ml-2'
                  >
                    Përdorues
                  </Badge>
                )}
                {exEmployee && (
                  <Badge
                    color='warning'
                    style={{
                      padding: '10px 20px',
                      borderRadius: '4px',
                      fontSize: '18px'
                    }}
                    className='ml-2'
                  >
                    Former employee at SPEEEX L.L.C
                  </Badge>
                )}
              </div>
              <div>
                <Button style={{ marginLeft: 10 }} type={'button'} onClick={() => history.push('/applicants')} color='primary'>
                  Back
                </Button>
              </div>
            </div>

            {applicant?.blacklist_match && !applicant?.blacklist_match_dismissed && (
              <div className='card card-default mb-3' style={{ borderLeft: '4px solid #f0ad4e' }}>
                <div className='card-body py-3 d-flex flex-column flex-md-row align-items-md-start justify-content-between'>
                  <div className='pr-md-3 flex-grow-1'>
                    <div style={{ fontWeight: 600, fontSize: '15px' }}>Potential blacklist match</div>
                    <div className='text-muted' style={{ fontSize: '13px', marginTop: '4px' }}>
                      {applicant?.blacklist_match_reason
                        ? `Matched by ${blacklistMatchReasonLabels[applicant.blacklist_match_reason] || applicant.blacklist_match_reason}. `
                        : ''}
                      Please verify against the blacklist before proceeding with this applicant.
                    </div>
                  </div>
                  <div style={alertActionColumnStyle} className='mt-3 mt-md-0'>
                    <Button
                      outline
                      color='warning'
                      size='sm'
                      onClick={handleDismissBlacklistAlert}
                    >
                      Mark as verified — not in blacklist
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {exEmployee && (
              <div className='card card-default mb-3' style={{ borderLeft: '4px solid #f0ad4e' }}>
                <div className='card-body py-3'>
                  <div className='d-flex flex-column flex-md-row align-items-md-start justify-content-between'>
                    <div className='pr-md-3 flex-grow-1'>
                      <div style={{ fontWeight: 600, fontSize: '15px' }}>Former employee at SPEEEX L.L.C</div>
                      <div className='text-muted' style={{ fontSize: '13px', marginTop: '4px' }}>
                        {applicant?.ex_employee_match_type
                          ? `${returningEmployeeMatchTypeLabels[applicant.ex_employee_match_type] || applicant.ex_employee_match_type}. `
                          : ''}
                        Review the previous employee profile(s) below before proceeding.
                      </div>
                    </div>
                    {applicant?.ex_employee_match_type === 'name' && (
                      <div style={alertActionColumnStyle} className='mt-3 mt-md-0'>
                        <Button
                          outline
                          color='warning'
                          size='sm'
                          onClick={handleDismissReturningEmployeeAlert}
                        >
                          Mark as verified — not a former employee
                        </Button>
                      </div>
                    )}
                  </div>
                  {formerEmployeeProfiles.length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      {formerEmployeeProfiles.map((profile) => (
                        <div
                          key={profile._id}
                          className='d-flex flex-column flex-md-row align-items-md-start justify-content-between'
                          style={{
                            padding: '10px 0',
                            borderTop: '1px solid #eee'
                          }}
                        >
                          <div className='pr-md-3 flex-grow-1'>
                            <div style={{ fontWeight: 600, fontSize: '14px' }}>
                              {profile.first_name} {profile.last_name}
                            </div>
                            <div className='text-muted' style={{ fontSize: '13px', marginTop: '2px' }}>
                              {profile.exit_date
                                ? `Exit date: ${formatKosovoDate(profile.exit_date)}`
                                : 'Exit date: N/A'}
                              {profile.exit_reason ? ` · ${profile.exit_reason}` : ''}
                            </div>
                          </div>
                          <div style={alertActionColumnStyle} className='mt-2 mt-md-0'>
                            <Link
                              to={`/editUsers/${profile._id}`}
                              className='btn btn-outline-warning btn-sm'
                            >
                              View employee profile
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <ApplicantHistoryLegend history={applicantHistory} loading={historyLoading} />

            <Row>
              <Col>
                <div className='card card-default'>
                  <div className='card-body'>
                    <div className='py-2'>
                      <div>
                        {applicant && (
                          <ApplicantDataTable
                            applicant={applicant}
                            applicantData={applicantData}
                            setApplicantData={setApplicantData}
                            applicantStatus={applicantStatus}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  {applicantStatus !== 'recruited' && (
                    <div
                      className='card-footer'
                      style={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-between'
                      }}
                    >
                      {buttonsApplicants.length > 0 && (
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap'
                          }}
                        >
                          {applicantStatus === 'new' && (
                            <Button
                              color='info'
                              className='mr-2 mb-1 mt-1'
                              onClick={() => setShowInterviewModal(true)}
                            >
                              Interview
                            </Button>
                          )}
                          {applicantStatus !== 'blacklist' ? (
                            buttonsApplicants.map((btn, index) => {
                              if (btn.showOnStatus.includes(applicantStatus)) {
                                return (
                                  <Button
                                    key={index}
                                    color='info'
                                    className='mr-2 mb-1 mt-1'
                                    onClick={() => {
                                      handleOpen()
                                      setStatus(btn.type)
                                      if (btn.type === 'failed' || btn.type === 'waiting_list') {
                                        setNewStatus(btn.type)
                                      }
                                    }}
                                  >
                                    {btn.text}
                                  </Button>
                                )
                              }
                            })
                          ) : (
                            <Badge
                              color='info'
                              style={{
                                padding: '10px 20px',
                                borderRadius: '2px',

                                fontWeight: '400',
                                fontSize: '16px'
                              }}
                            >
                              This person is on the blacklist.
                            </Badge>
                          )}
                        </div>
                      )}

                      <div>
                        <Button
                          color='info'
                          className='mr-2 mb-1 mt-1'
                          onClick={() => {
                            rollbackApplicantStatus(true)
                            setDescription('')
                          }}
                        >
                          Return as applicant
                        </Button>

                        <Button
                          color='success'
                          className='mr-2 mb-1 mt-1'
                          onClick={() => {
                            changeApplicantStatus(true)
                            setDescription('')
                          }}
                        >
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </div>
        ) : (
          <Loader />
        )}
      </ContentWrapper>
    )
  )
}

export default Applicant
